import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Skill } from '../../types';

interface SkillsAutocompleteProps {
  value: Skill[];
  onChange: (skills: Skill[]) => void;
  placeholder?: string;
  maxSkills?: number;
  readOnly?: boolean;
  userRole?: 'TRADESPERSON' | 'HOMEOWNER';
}

const SkillsAutocomplete: React.FC<SkillsAutocompleteProps> = ({
  value = [],
  onChange,
  placeholder = "Type to search skills...",
  maxSkills = 20,
  readOnly = false,
  userRole = 'TRADESPERSON'
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<Skill[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Comprehensive skills database
  const allSkills = useMemo(() => [
    // Plumbing
    { id: 'plumbing-general', name: 'Plumbing', category: 'Plumbing' },
    { id: 'pipe-fitting', name: 'Pipe Fitting', category: 'Plumbing' },
    { id: 'boiler-repair', name: 'Boiler Repair', category: 'Plumbing' },
    { id: 'bathroom-installation', name: 'Bathroom Installation', category: 'Plumbing' },
    { id: 'central-heating', name: 'Central Heating', category: 'Plumbing' },
    { id: 'leak-detection', name: 'Leak Detection', category: 'Plumbing' },
    { id: 'drain-unblocking', name: 'Drain Unblocking', category: 'Plumbing' },
    
    // Electrical
    { id: 'electrical-general', name: 'Electrical Work', category: 'Electrical' },
    { id: 'rewiring', name: 'Rewiring', category: 'Electrical' },
    { id: 'fuse-box-upgrade', name: 'Fuse Box Upgrade', category: 'Electrical' },
    { id: 'socket-installation', name: 'Socket Installation', category: 'Electrical' },
    { id: 'lighting-installation', name: 'Lighting Installation', category: 'Electrical' },
    { id: 'electrical-testing', name: 'Electrical Testing', category: 'Electrical' },
    { id: 'smart-home-installation', name: 'Smart Home Installation', category: 'Electrical' },
    
    // Carpentry
    { id: 'carpentry-general', name: 'Carpentry', category: 'Carpentry' },
    { id: 'kitchen-fitting', name: 'Kitchen Fitting', category: 'Carpentry' },
    { id: 'door-hanging', name: 'Door Hanging', category: 'Carpentry' },
    { id: 'window-installation', name: 'Window Installation', category: 'Carpentry' },
    { id: 'flooring', name: 'Flooring', category: 'Carpentry' },
    { id: 'built-in-wardrobes', name: 'Built-in Wardrobes', category: 'Carpentry' },
    { id: 'decking', name: 'Decking', category: 'Carpentry' },
    { id: 'skirting-boards', name: 'Skirting Boards', category: 'Carpentry' },
    
    // Painting & Decorating
    { id: 'painting-general', name: 'Painting & Decorating', category: 'Painting' },
    { id: 'interior-painting', name: 'Interior Painting', category: 'Painting' },
    { id: 'exterior-painting', name: 'Exterior Painting', category: 'Painting' },
    { id: 'wallpapering', name: 'Wallpapering', category: 'Painting' },
    { id: 'spray-painting', name: 'Spray Painting', category: 'Painting' },
    { id: 'furniture-painting', name: 'Furniture Painting', category: 'Painting' },
    
    // Building & Construction
    { id: 'building-general', name: 'General Building', category: 'Building' },
    { id: 'bricklaying', name: 'Bricklaying', category: 'Building' },
    { id: 'plastering', name: 'Plastering', category: 'Building' },
    { id: 'roofing', name: 'Roofing', category: 'Building' },
    { id: 'extensions', name: 'Extensions', category: 'Building' },
    { id: 'loft-conversions', name: 'Loft Conversions', category: 'Building' },
    { id: 'groundwork', name: 'Groundwork', category: 'Building' },
    { id: 'scaffolding', name: 'Scaffolding', category: 'Building' },
    
    // Tiling
    { id: 'tiling-general', name: 'Tiling', category: 'Tiling' },
    { id: 'bathroom-tiling', name: 'Bathroom Tiling', category: 'Tiling' },
    { id: 'kitchen-tiling', name: 'Kitchen Tiling', category: 'Tiling' },
    { id: 'floor-tiling', name: 'Floor Tiling', category: 'Tiling' },
    { id: 'wall-tiling', name: 'Wall Tiling', category: 'Tiling' },
    
    // Landscaping & Garden
    { id: 'landscaping-general', name: 'Landscaping', category: 'Garden' },
    { id: 'garden-design', name: 'Garden Design', category: 'Garden' },
    { id: 'paving', name: 'Paving', category: 'Garden' },
    { id: 'fencing', name: 'Fencing', category: 'Garden' },
    { id: 'tree-surgery', name: 'Tree Surgery', category: 'Garden' },
    { id: 'artificial-grass', name: 'Artificial Grass', category: 'Garden' },
    
    // Cleaning
    { id: 'cleaning-general', name: 'General Cleaning', category: 'Cleaning' },
    { id: 'end-of-tenancy', name: 'End of Tenancy Cleaning', category: 'Cleaning' },
    { id: 'carpet-cleaning', name: 'Carpet Cleaning', category: 'Cleaning' },
    { id: 'window-cleaning', name: 'Window Cleaning', category: 'Cleaning' },
    { id: 'pressure-washing', name: 'Pressure Washing', category: 'Cleaning' },
    
    // Specialist
    { id: 'gas-safety', name: 'Gas Safety', category: 'Specialist' },
    { id: 'asbestos-removal', name: 'Asbestos Removal', category: 'Specialist' },
    { id: 'damp-proofing', name: 'Damp Proofing', category: 'Specialist' },
    { id: 'insulation', name: 'Insulation', category: 'Specialist' },
    { id: 'security-systems', name: 'Security Systems', category: 'Specialist' },
    { id: 'solar-panels', name: 'Solar Panel Installation', category: 'Specialist' },
  ], []);

  // Filter suggestions based on input
  useEffect(() => {
    if (inputValue.trim().length < 1) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    const filtered = allSkills.filter(skill => {
      const matchesSearch = skill.name.toLowerCase().includes(inputValue.toLowerCase());
      const notAlreadySelected = !value.some(selectedSkill => selectedSkill.id === skill.id);
      return matchesSearch && notAlreadySelected;
    }).slice(0, 8); // Limit to 8 suggestions

    setSuggestions(filtered);
    setIsOpen(filtered.length > 0);
    setHighlightedIndex(-1);
  }, [inputValue, value, allSkills]);

  // Handle clicks outside component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          const selectedSkill = suggestions[highlightedIndex];
          if (selectedSkill) {
            addSkill(selectedSkill);
          }
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const addSkill = (skill: Skill) => {
    if (value.length >= maxSkills) return;
    
    const newSkills = [...value, skill];
    onChange(newSkills);
    setInputValue('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  const removeSkill = (skillToRemove: Skill) => {
    const newSkills = value.filter(skill => skill.id !== skillToRemove.id);
    onChange(newSkills);
  };

  const groupedSuggestions = suggestions.reduce((groups, skill) => {
    const category = skill.category || 'Other';
    (groups[category] = groups[category] || []).push(skill);
    return groups;
  }, {} as Record<string, Skill[]>);

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Skills & Expertise
          {maxSkills && (
            <span className="text-gray-500 ml-1">
              ({value.length}/{maxSkills})
            </span>
          )}
        </label>

        {/* Selected Skills */}
        {value.length > 0 && (
          <div className="flex flex-wrap gap-2 p-2 bg-gray-50 border border-gray-200 rounded-md min-h-[40px]">
            {value.map((skill) => (
              <div
                key={skill.id}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200"
              >
                {skill.name}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => removeSkill(skill)}
                    className="ml-2 text-blue-600 hover:text-blue-800 focus:outline-none"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Input */}
        {!readOnly && value.length < maxSkills && (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        )}

        {/* Suggestions Dropdown */}
        {isOpen && suggestions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {Object.entries(groupedSuggestions).map(([category, categorySkills]) => (
              <div key={category}>
                {/* Category Header */}
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  {category}
                </div>
                
                {/* Category Skills */}
                {categorySkills.map((skill) => {
                  const globalIndex = suggestions.findIndex(s => s.id === skill.id);
                  return (
                    <button
                      key={skill.id}
                      type="button"
                      onClick={() => addSkill(skill)}
                      className={`w-full text-left px-3 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 ${
                        highlightedIndex === globalIndex ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{skill.name}</span>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!readOnly && value.length === 0 && (
          <p className="text-sm text-gray-500">
            No skills added yet. Start typing to search and add skills.
          </p>
        )}

        {/* Read-only Empty State */}
        {readOnly && value.length === 0 && (
          <p className="text-sm text-gray-500">
            No skills specified.
          </p>
        )}
      </div>
    </div>
  );
};

export default SkillsAutocomplete;
